/* eslint-disable @typescript-eslint/no-explicit-any */
import type { UseMutationOptions } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { mediumRepository } from "../repositories/mediumRepository";
import type { MediumSignupPayload } from "../types";
import { sessionQueryKey } from "./useSessionQuery";

export type MediumSignupError = {
  message: string;
  code?: string;
  fields?: Record<string, string | string[]>;
};

export type MediumSignupResponse = {
  message?: string;
  user?: {
    profile?: Record<string, unknown>;
    terreiro?: {
      id?: string;
      [k: string]: unknown;
    };
    medium?: {
      id?: string;
      [k: string]: unknown;
    };
    [k: string]: unknown;
  };
  [k: string]: unknown;
};

type ApiErrorPayload = {
  error?: {
    code?: string;
    message?: string;
    field?: string;
    details?: string;
  };
  code?: string;
  missing?: string[];
  errors?: Record<string, string | string[]>;
  fieldErrors?: Record<string, string | string[]>;
  message?: string;
};

const GENERIC_SIGNUP_ERROR_MESSAGE =
  "Não foi possível concluir o cadastro. Tente novamente.";

const EMAIL_ALREADY_EXISTS_MESSAGE =
  "E-mail já cadastrado. Faça login ou use outro e-mail.";

function extractApiErrorCode(data: unknown): string | undefined {
  if (!data || typeof data !== "object") return undefined;
  const d = data as ApiErrorPayload;
  if (typeof d.code === "string") return d.code;
  if (typeof d.error?.code === "string") return d.error.code;
  return undefined;
}

/* Helpers */
function apiMessage(data: unknown): string | undefined {
  if (!data || typeof data !== "object") return undefined;
  const d = data as ApiErrorPayload;
  if (
    d.error &&
    typeof d.error === "object" &&
    typeof d.error.message === "string"
  ) {
    return d.error.message;
  }
  if (typeof d.message === "string") return d.message;
  return undefined;
}

function mapApiFieldError(
  data: unknown
): Record<string, string | string[]> | undefined {
  if (!data || typeof data !== "object") return undefined;
  const d = data as ApiErrorPayload;

  if (
    d.error &&
    typeof d.error === "object" &&
    typeof d.error.field === "string"
  ) {
    const fieldName = d.error.field;
    const fieldMessage =
      typeof d.error.message === "string"
        ? d.error.message
        : typeof d.error.code === "string"
        ? d.error.code
        : "error";
    return { [fieldName]: fieldMessage };
  }

  if (Array.isArray(d.missing)) {
    const obj: Record<string, string> = {};
    for (const f of d.missing) if (typeof f === "string") obj[f] = "required";
    return Object.keys(obj).length ? obj : undefined;
  }

  if (d.errors && typeof d.errors === "object") return d.errors;
  if (d.fieldErrors && typeof d.fieldErrors === "object") return d.fieldErrors;

  return undefined;
}

export function normalizeMediumSignupError(err: unknown): MediumSignupError {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as unknown;
    const code = extractApiErrorCode(data);

    if (code === "EMAIL_ALREADY_EXISTS") {
      return {
        code: "EMAIL_ALREADY_EXISTS",
        message: EMAIL_ALREADY_EXISTS_MESSAGE,
        fields: { email: EMAIL_ALREADY_EXISTS_MESSAGE },
      };
    }

    if (code === "UNIQUE_CONSTRAINT_VIOLATION") {
      return {
        code: "SIGNUP_ERROR",
        message: GENERIC_SIGNUP_ERROR_MESSAGE,
      };
    }

    const fields = mapApiFieldError(data);
    const message = apiMessage(data) ?? err.message ?? GENERIC_SIGNUP_ERROR_MESSAGE;

    const result: MediumSignupError = { message };
    if (fields) result.fields = fields;
    if (code) result.code = code;
    return result;
  }

  if (
    err &&
    typeof err === "object" &&
    "message" in (err as Record<string, unknown>)
  ) {
    const m = (err as Record<string, unknown>)["message"];
    return { message: typeof m === "string" ? m : String(m) };
  }

  return { message: GENERIC_SIGNUP_ERROR_MESSAGE };
}

/* useMediumSignupMutation */
export const useMediumSignupMutation = (
  options?: UseMutationOptions<
    MediumSignupResponse,
    MediumSignupError,
    MediumSignupPayload
  >
) => {
  const queryClient = useQueryClient();

  const wrappedOnSuccess: UseMutationOptions<
    MediumSignupResponse,
    MediumSignupError,
    MediumSignupPayload
  >["onSuccess"] = async (data, variables, context) => {
    try {
      if (data?.user) {
        // Update session cache so AuthContext/ProtectedRoute sees auth immediately
        queryClient.setQueryData(sessionQueryKey, data.user);

        // Also update legacy caches for profile/terreiro if needed
        const profile = data.user.profile;
        const terreiro = data.user.terreiro;
        const terreiroId = terreiro?.id;
        const medium = data.user.medium;
        const mediumId = medium?.id;

        if (profile) {
          queryClient.setQueryData(["auth", "currentUser"], profile);
          try {
            localStorage.setItem(
              "ilovemacumba:auth:currentUser",
              JSON.stringify(profile)
            );
          } catch {
            // ignore localStorage errors
          }
        }

        if (terreiroId) {
          queryClient.setQueryData(["terreiro", terreiroId], terreiro);
          queryClient.setQueryData(["terreiro", "current"], terreiro);
          try {
            // Persist only the global current terreiro key; do not store by id.
            localStorage.setItem(
              "ilovemacumba:terreiro:current",
              JSON.stringify(terreiro)
            );
          } catch {
            // ignore localStorage errors
          }
        }

        // Save medium data to local storage when onboarding a new medium
        if (mediumId && medium) {
          queryClient.setQueryData(["medium", mediumId], medium);
          queryClient.setQueryData(["medium", "current"], medium);
          try {
            localStorage.setItem(
              "ilovemacumba:medium:current",
              JSON.stringify(medium)
            );
          } catch {
            // ignore localStorage errors
          }
        }
      } else {
        // ensure at least non-null value until /auth/me refetch
        queryClient.setQueryData(sessionQueryKey, null);
      }

      // Trigger a background refetch to confirm session via /v1/auth/me
      await queryClient.invalidateQueries({ queryKey: sessionQueryKey });
    } catch {
      // swallow cache/localStorage errors
    }

    options?.onSuccess?.(data, variables, context as any);
  };

  return useMutation<
    MediumSignupResponse,
    MediumSignupError,
    MediumSignupPayload
  >({
    mutationFn: (data: MediumSignupPayload) =>
      mediumRepository.signup(data as MediumSignupPayload).catch((err) => {
        throw normalizeMediumSignupError(err);
      }),
    ...options,
    onSuccess: wrappedOnSuccess,
  });
};

export default useMediumSignupMutation;
